import { supabase } from '../supabase.js';
import { hasPart } from '../lib/access.js';

/* ---------------------------------------------------------------
   Lookup cache — fetched once per session
--------------------------------------------------------------- */
let _lookups = null;

async function getLookups() {
  if (_lookups) return _lookups;
  const [progs, munis, towns, venues, classes] = await Promise.all([
    supabase.from('programmes').select('programme_id,programme_name').order('programme_name'),
    supabase.from('municipalities').select('municipality_id,municipality_name').order('municipality_name'),
    supabase.from('towns').select('town_id,town_name,municipality_id').order('town_name'),
    supabase.from('training_venues').select('training_venue_id,training_venue_name,town_id').order('training_venue_name'),
    supabase.from('classes').select('class_id,class_name,programme_id').order('class_name'),
  ]);
  _lookups = {
    programmes:    progs.data   || [],
    municipalities: munis.data  || [],
    towns:         towns.data   || [],
    venues:        venues.data  || [],
    classes:       classes.data || [],
  };
  return _lookups;
}

/* ---------------------------------------------------------------
   Public API
   studentUuid: null → create mode   uuid → edit mode
   onSave: called with { studentUuid } after save
--------------------------------------------------------------- */
export async function openEditStudentModal({ studentUuid = null, onSave = null } = {}) {
  const isCreate = !studentUuid;

  const overlay = document.createElement('div');
  overlay.className = 'sig-modal-overlay';
  overlay.innerHTML = `
    <div class="sig-modal" style="max-width:640px;width:92vw;max-height:92vh;overflow-y:auto;display:flex;flex-direction:column">
      <div class="sig-modal-head">
        <div class="sig-modal-title">${isCreate ? '+ Add New Learner' : 'Edit Learner'}</div>
      </div>
      <div id="esm-body" style="padding:20px 24px;flex:1;overflow-y:auto">
        <div style="text-align:center;padding:32px;color:var(--text-muted)">Loading…</div>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  /* Click outside → close */
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

  const [lookups, studentResult, enrolResult] = await Promise.all([
    getLookups(),
    isCreate
      ? Promise.resolve({ data: {}, error: null })
      : supabase.from('students').select('*').eq('student_uuid', studentUuid).single(),
    isCreate
      ? Promise.resolve({ data: [], error: null })
      : supabase.from('student_class_enrollments')
          .select('enrollment_id,class_id,status,enrollment_date')
          .eq('student_uuid', studentUuid),
  ]);

  const body = document.getElementById('esm-body');
  if (studentResult.error) {
    body.innerHTML = `<div style="color:var(--danger);padding:8px">Error: ${esc(studentResult.error.message)}</div>`;
    return;
  }

  const s          = studentResult.data || {};
  const isAdmin    = hasPart('learners');   // every learner's full record
  const enrolments = enrolResult.data  || [];

  body.innerHTML = buildForm(s, lookups, isAdmin, isCreate, enrolments);

  /* Wire disability toggle */
  body.querySelector('#esm-disability').addEventListener('change', e => {
    body.querySelector('#esm-dt-row').style.display = e.target.checked ? '' : 'none';
  });

  /* Wire status change → show/hide reason field */
  body.querySelector('#esm-status')?.addEventListener('change', e => {
    const reasonRow = body.querySelector('#esm-status-reason-row');
    if (reasonRow) reasonRow.style.display = e.target.value !== e.target.dataset.original ? '' : 'none';
    if (e.target.value === e.target.dataset.original) {
      const ta = body.querySelector('#esm-status-reason');
      if (ta) ta.value = '';
    }
  });

  /* Wire tax-status → directive % visibility */
  body.querySelector('#tax_status')?.addEventListener('change', e => {
    const row = body.querySelector('#esm-directive-row');
    if (row) row.style.display = e.target.value === 'Directive' ? '' : 'none';
  });

  /* Wire enrolment remove / undo buttons */
  body.querySelectorAll('.esm-enrol-remove').forEach(btn => {
    btn.addEventListener('click', () => {
      const row     = btn.closest('.esm-enrol-row');
      const marking = row.dataset.removed !== 'true';
      row.dataset.removed = marking ? 'true' : 'false';
      row.style.opacity   = marking ? '0.35' : '1';
      row.querySelector('.esm-enrol-class').disabled = marking;
      btn.textContent = marking ? 'Undo' : '✕ Remove';
    });
  });

  body.querySelector('#esm-cancel').addEventListener('click', () => overlay.remove());
  body.querySelector('#esm-save').addEventListener('click', () =>
    handleSave({ overlay, body, lookups, studentUuid, isCreate, isAdmin, onSave })
  );
}

/* ---------------------------------------------------------------
   Form HTML builder
--------------------------------------------------------------- */
function buildForm(s, lookups, isAdmin, isCreate, enrolments = []) {
  const v = key => esc(s[key] ?? '');

  const opts = (arr, valueKey, labelKey) =>
    arr.map(o => `<option value="${esc(o[valueKey])}" ${o[valueKey] == s[valueKey] ? 'selected' : ''}>${esc(o[labelKey])}</option>`).join('');

  const si = `style="width:100%"`;

  function field(label, inputHtml, hint = '') {
    return `<div style="margin-bottom:12px">
      <label style="display:block;font-size:.78rem;font-weight:600;color:var(--text-muted);margin-bottom:4px">${label}</label>
      ${inputHtml}
      ${hint ? `<div style="font-size:.72rem;color:var(--text-muted);margin-top:2px">${hint}</div>` : ''}
    </div>`;
  }

  const text  = (id, lbl, req = false) => field(lbl,
    `<input type="text" id="${id}" class="search-input" ${si} value="${v(id)}"${req ? ' required' : ''}>`
  );
  const date  = (id, lbl) => field(lbl,
    `<input type="date" id="${id}" class="search-input" ${si} value="${v(id)}">`
  );
  const selEl = (id, lbl, optionsHtml) => field(lbl,
    `<select id="${id}" class="select-input" ${si}><option value="">— select —</option>${optionsHtml}</select>`
  );

  const section = (title, body) => `
    <div style="margin-bottom:20px">
      <div style="font-size:.75rem;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:var(--text-muted);padding-bottom:5px;margin-bottom:12px;border-bottom:1px solid var(--border-light)">${title}</div>
      ${body}
    </div>`;

  const genderOpts = ['Male','Female','Other'].map(g =>
    `<option value="${g}" ${g === s.gender ? 'selected' : ''}>${g}</option>`).join('');
  const raceOpts   = ['African','Black','Coloured','Indian','White','Asian','Other'].map(r =>
    `<option value="${r}" ${r === s.race ? 'selected' : ''}>${r}</option>`).join('');
  const statusOpts = [
    ['active','Active'],['withdrawn','Dropout'],['graduated','Graduated'],
    ['suspended','Suspended'],['transferred','Transferred'],['inactive','Inactive'],
  ].map(([v2, lbl]) =>
    `<option value="${v2}" ${v2 === (s.status || 'active') ? 'selected' : ''}>${lbl}</option>`).join('');

  const progOpts  = opts(lookups.programmes,    'programme_id',     'programme_name');
  const muniOpts  = opts(lookups.municipalities, 'municipality_id',  'municipality_name');
  const townOpts  = opts(lookups.towns,          'town_id',          'town_name');
  const venueOpts = opts(lookups.venues,         'training_venue_id','training_venue_name');
  const classOpts = lookups.classes.map(c =>
    `<option value="${esc(c.class_id)}">${esc(c.class_name)}</option>`).join('');

  return `
    ${section('Personal', `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 16px">
        ${text('first_name', 'First Name *', true)}
        ${text('surname',    'Surname *',    true)}
      </div>
      ${text('national_id', 'ID Number (national_id) *', true)}
      ${date('date_of_birth', 'Date of Birth')}
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 16px">
        ${selEl('gender', 'Gender', genderOpts)}
        ${selEl('race',   'Race',   raceOpts)}
      </div>
      <div style="margin-bottom:12px">
        <label style="display:flex;align-items:center;gap:8px;font-size:.82rem;cursor:pointer">
          <input type="checkbox" id="esm-disability" ${s.disability ? 'checked' : ''} style="width:14px;height:14px">
          Has disability
        </label>
      </div>
      <div id="esm-dt-row" style="${s.disability ? '' : 'display:none'}">
        ${text('disability_type', 'Disability type')}
      </div>
      ${text('highest_qualification', 'Highest Qualification')}
    `)}
    ${section('Contact', `
      ${text('email',                'Email')}
      ${text('phone_number',         'Phone Number')}
      ${text('residential_address',  'Residential Address')}
    `)}
    ${section('Programme & Location', `
      ${selEl('programme_id',              'Programme',     progOpts)}
      ${selEl('municipality_id',           'Municipality',  muniOpts)}
      ${selEl('town_id',                   'Town',          townOpts)}
      ${selEl('current_training_venue_id', 'Training Venue',venueOpts)}
    `)}
    ${section('Employment Info', `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 16px">
        ${text('employee_number', 'Employee Number')}
        ${text('uif_ref_number',  'UIF Ref Number')}
      </div>
      ${text('tax_ref_number', 'Tax Ref Number')}
      ${date('start_date', 'Programme Start Date')}
    `)}
    ${section('Banking', `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 16px">
        ${text('bank_name',           'Bank Name')}
        ${text('bank_account_number', 'Account Number')}
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 16px">
        ${selEl('bank_account_type', 'Account Type',
          ['Cheque','Savings','Transmission','Bond'].map(t =>
            `<option value="${t}" ${t === s.bank_account_type ? 'selected' : ''}>${t}</option>`
          ).join('')
        )}
        ${text('branch_code', 'Branch Code')}
      </div>
      <div style="margin-bottom:12px">
        <label style="display:flex;align-items:center;gap:8px;font-size:.82rem;cursor:pointer">
          <input type="checkbox" id="esm-uif" ${s.uif_contributor ? 'checked' : ''} style="width:14px;height:14px">
          UIF Contributor
        </label>
      </div>
    `)}
    ${section('Payroll Details', `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 16px">
        ${selEl('marital_status', 'Marital Status',
          ['Single','Married','Divorced','Widowed'].map(t =>
            `<option value="${t}" ${t === s.marital_status ? 'selected' : ''}>${t}</option>`
          ).join('')
        )}
        ${selEl('payment_method', 'Payment Method',
          ['EFT','Cash','Cheque'].map(t =>
            `<option value="${t}" ${t === s.payment_method ? 'selected' : ''}>${t}</option>`
          ).join('')
        )}
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 16px">
        ${selEl('tax_status', 'Tax Status',
          ['Normal','Directive','Exempt','No Number'].map(t =>
            `<option value="${t}" ${t === s.tax_status ? 'selected' : ''}>${t}</option>`
          ).join('')
        )}
        <div id="esm-directive-row" style="${s.tax_status === 'Directive' ? '' : 'display:none'}">
          ${field('Directive %',
            `<input type="number" id="directive_pct" class="search-input" ${si} min="0" max="100" step="0.01" value="${v('directive_pct')}">`
          )}
        </div>
      </div>
    `)}
    ${section('Status', `
      <select id="esm-status" class="select-input" style="width:100%"
        data-original="${esc(s.status || 'active')}">
        ${statusOpts}
      </select>
      <div style="margin-top:10px">
        ${date('termination_date', 'Termination Date')}
      </div>
      <div id="esm-status-reason-row" style="display:none;margin-top:10px">
        <label style="font-size:.8rem;font-weight:600;color:var(--text-muted);display:block;margin-bottom:4px">
          Reason for status change <span style="color:var(--danger)">*</span>
        </label>
        <textarea id="esm-status-reason" class="search-input" rows="2"
          style="width:100%;box-sizing:border-box;resize:vertical;font-size:.85rem"
          placeholder="Required — explain why the status is being changed…"></textarea>
      </div>
    `)}
    ${isCreate ? section('Enrolment (optional)', `
      <p style="font-size:.8rem;color:var(--text-muted);margin:-4px 0 10px">Enrol this learner in a class right away.</p>
      <select id="esm-class" class="select-input" style="width:100%">
        <option value="">— skip enrolment —</option>
        ${classOpts}
      </select>
    `) : ''}
    ${!isCreate ? section('Enrolments', `
      <p style="font-size:.78rem;color:var(--text-muted);margin:-4px 0 10px">
        Change the dropdown to reassign to a different class. Click <strong>✕ Remove</strong> to unenrol.
        Both take effect on <strong>Save Changes</strong>.
      </p>
      ${enrolments.length
        ? enrolments.map(e => {
            const classOpts2 = lookups.classes.map(c =>
              `<option value="${esc(c.class_id)}" ${c.class_id === e.class_id ? 'selected' : ''}>${esc(c.class_name)}</option>`
            ).join('');
            const statusBadge = e.status && e.status !== 'enrolled'
              ? `<span style="font-size:.7rem;color:var(--text-muted);white-space:nowrap">${esc(e.status)}</span>`
              : '';
            return `
              <div class="esm-enrol-row" data-enrollment-id="${esc(e.enrollment_id)}" data-original-class="${esc(e.class_id)}"
                   style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
                <select class="select-input esm-enrol-class" style="flex:1;min-width:0">
                  ${classOpts2}
                </select>
                ${statusBadge}
                <button type="button" class="btn btn-ghost esm-enrol-remove"
                        style="font-size:.75rem;padding:4px 10px;color:var(--danger);white-space:nowrap;flex-shrink:0">
                  ✕ Remove
                </button>
              </div>`;
          }).join('')
        : `<p style="font-size:.82rem;color:var(--text-muted);margin-bottom:10px">Not enrolled in any class.</p>`
      }
      <div style="display:flex;align-items:center;gap:8px;margin-top:8px;padding-top:8px;border-top:1px solid var(--border-light)">
        <label style="font-size:.78rem;font-weight:600;color:var(--text-muted);white-space:nowrap;flex-shrink:0">+ Enrol in class</label>
        <select id="esm-add-class" class="select-input" style="flex:1;min-width:0">
          <option value="">— select class —</option>
          ${lookups.classes.map(c => `<option value="${esc(c.class_id)}">${esc(c.class_name)}</option>`).join('')}
        </select>
      </div>
    `) : ''}
    <div id="esm-error" style="color:var(--danger);font-size:.8rem;text-align:right;margin-bottom:6px"></div>
    <div style="display:flex;justify-content:flex-end;gap:8px;padding-top:16px;border-top:1px solid var(--border-light);margin-top:8px;position:sticky;bottom:0;background:var(--surface)">
      <button id="esm-cancel" class="btn btn-ghost">Cancel</button>
      <button id="esm-save"   class="btn btn-primary">${isCreate ? 'Create Learner' : 'Save Changes'}</button>
    </div>
  `;
}

/* ---------------------------------------------------------------
   Save handler
--------------------------------------------------------------- */
async function handleSave({ overlay, body, lookups, studentUuid, isCreate, isAdmin, onSave }) {
  const g   = id => body.querySelector(`#${id}`);
  const val = id => g(id)?.value?.trim() ?? '';

  const firstName  = val('first_name');
  const surname    = val('surname');
  const nationalId = val('national_id');
  const errEl      = g('esm-error');

  if (!firstName || !surname || (isCreate && !nationalId)) {
    errEl.textContent = isCreate
      ? 'First Name, Surname and ID Number are required.'
      : 'First Name and Surname are required.';
    return;
  }

  const statusSel    = g('esm-status');
  const newStatus    = statusSel?.value || 'active';
  const origStatus   = statusSel?.dataset.original || 'active';
  const statusReason = (g('esm-status-reason')?.value || '').trim();
  if (!isCreate && newStatus !== origStatus && !statusReason) {
    errEl.textContent = 'Please provide a reason for the status change.';
    return;
  }

  const payload = {
    first_name:               firstName,
    surname,
    national_id:              nationalId || null,
    date_of_birth:            val('date_of_birth') || null,
    gender:                   val('gender')        || null,
    race:                     val('race')          || null,
    disability:               g('esm-disability')?.checked ?? false,
    disability_type:          val('disability_type')       || null,
    highest_qualification:    val('highest_qualification') || null,
    email:                    val('email')                 || null,
    phone_number:             val('phone_number')          || null,
    residential_address:      val('residential_address')   || null,
    programme_id:             val('programme_id')          || null,
    municipality_id:          val('municipality_id')       || null,
    town_id:                  val('town_id')               || null,
    current_training_venue_id: val('current_training_venue_id') || null,
    employee_number:          val('employee_number')  || null,
    uif_ref_number:           val('uif_ref_number')   || null,
    tax_ref_number:           val('tax_ref_number')   || null,
    start_date:               val('start_date')       || null,
    termination_date:         val('termination_date') || null,
    bank_name:                val('bank_name')             || null,
    bank_account_number:      val('bank_account_number')   || null,
    bank_account_type:        val('bank_account_type')     || null,
    branch_code:              val('branch_code')            || null,
    uif_contributor:          g('esm-uif')?.checked        ?? false,
    marital_status:           val('marital_status')         || null,
    tax_status:               val('tax_status')             || null,
    directive_pct:            val('directive_pct') ? Number(val('directive_pct')) : null,
    payment_method:           val('payment_method')         || null,
  };
  payload.status = newStatus;
  if (!isCreate && newStatus !== origStatus && statusReason) {
    const date = new Date().toLocaleDateString('en-ZA', { day:'2-digit', month:'short', year:'numeric' });
    payload.notes = `[${date}] Status changed to ${newStatus}: ${statusReason}`;
  }

  const saveBtn = g('esm-save');
  saveBtn.disabled    = true;
  saveBtn.textContent = 'Saving…';
  errEl.textContent   = '';

  let savedUuid = studentUuid;

  if (isCreate) {
    const { data, error } = await supabase.from('students').insert(payload).select('student_uuid').single();
    if (error) { errEl.textContent = error.message; saveBtn.disabled = false; saveBtn.textContent = 'Create Learner'; return; }
    savedUuid = data.student_uuid;

    const classId = val('esm-class');
    if (classId) {
      const { error: enrErr } = await supabase.from('student_class_enrollments')
        .insert({ student_uuid: savedUuid, class_id: classId, status: 'enrolled' });
      if (enrErr) { errEl.textContent = `Class enrolment failed: ${enrErr.message}`; saveBtn.disabled = false; saveBtn.textContent = 'Create Learner'; return; }
    }
  } else {
    const { data: updated, error } = await supabase.from('students').update(payload).eq('student_uuid', studentUuid).select('student_uuid');
    if (error) {
      const msg = error.message || '';
      errEl.textContent = msg.includes('students_national_id_key')
        ? 'That ID number is already registered to another learner. Please check for duplicates.'
        : msg.includes('students_email_key')
          ? 'That email address is already registered to another learner.'
          : msg;
      saveBtn.disabled = false; saveBtn.textContent = 'Save Changes'; return;
    }
    if (!updated?.length) {
      errEl.textContent = 'Save failed — you may not have permission to edit this learner, or the record no longer exists.';
      saveBtn.disabled = false; saveBtn.textContent = 'Save Changes'; return;
    }

    // Process enrolment reassignments and removals
    for (const row of body.querySelectorAll('.esm-enrol-row')) {
      const enrollmentId  = row.dataset.enrollmentId;
      const originalClass = row.dataset.originalClass;
      if (row.dataset.removed === 'true') {
        const { error: delErr } = await supabase.from('student_class_enrollments').delete().eq('enrollment_id', enrollmentId);
        if (delErr) { errEl.textContent = `Class removal failed: ${delErr.message}`; saveBtn.disabled = false; saveBtn.textContent = 'Save Changes'; return; }
      } else {
        const newClassId = row.querySelector('.esm-enrol-class')?.value;
        if (newClassId && newClassId !== originalClass) {
          const { error: updErr } = await supabase.from('student_class_enrollments')
            .update({ class_id: newClassId })
            .eq('enrollment_id', enrollmentId);
          if (updErr) { errEl.textContent = `Class change failed: ${updErr.message}`; saveBtn.disabled = false; saveBtn.textContent = 'Save Changes'; return; }
        }
      }
    }

    // Add new enrolment if a class was selected
    const addClassId = g('esm-add-class')?.value;
    if (addClassId) {
      const { error: insErr } = await supabase.from('student_class_enrollments')
        .insert({ student_uuid: studentUuid, class_id: addClassId, status: 'enrolled' });
      if (insErr) { errEl.textContent = `Class enrolment failed: ${insErr.message}`; saveBtn.disabled = false; saveBtn.textContent = 'Save Changes'; return; }
    }
  }

  overlay.remove();
  window.dispatchEvent(new CustomEvent('learner:updated', { detail: { studentUuid: savedUuid } }));
  if (onSave) onSave({ studentUuid: savedUuid });
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
}
